'use client';

import type { ArchitectureState, DotStatus } from '@/lib/architecture-state';

const DOT: Record<DotStatus, string> = {
  ok: 'bg-emerald-400 shadow-[0_0_6px_rgba(74,222,128,0.55)]',
  warn: 'bg-amber-300 shadow-[0_0_6px_rgba(252,211,77,0.5)]',
  fail: 'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.55)]',
  na: 'bg-zinc-700',
};

const VALUE_TONE: Record<DotStatus, string> = {
  ok: 'text-white/85',
  warn: 'text-amber-300',
  fail: 'text-red-300',
  na: 'text-white/40',
};

// Render weekday/day labels above each column so the strip reads at a glance.
function shortDate(iso: string): string {
  // iso = YYYY-MM-DD
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][dt.getUTCDay()];
  return `${wd} ${m}/${d}`;
}

interface Props {
  windowDates: string[];
  rows: ArchitectureState['dailyHealth'];
}

export function DailyHealthGrid({ windowDates, rows }: Props): React.JSX.Element {
  if (windowDates.length === 0 || rows.length === 0) {
    return (
      <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-4">
        <div className="text-[11px] uppercase tracking-wider text-[#c9a84c]">Daily Health</div>
        <div className="mt-3 text-[11px] text-white/40">No data in window.</div>
      </div>
    );
  }

  const cols = windowDates.length;

  return (
    <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-4">
      <div className="flex items-baseline justify-between">
        <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">Daily Health</div>
        <div className="text-[10px] text-white/40">Trailing 5 cron days</div>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[9px] uppercase tracking-wider text-white/40">
              <th className="text-left font-normal pb-2 pr-3 w-[150px]">Metric</th>
              {windowDates.map((d) => (
                <th
                  key={d}
                  className="text-right font-normal pb-2 px-2 tabular-nums"
                  style={{ width: `${(100 - 20) / cols}%` }}
                >
                  {shortDate(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.metric} className="border-t border-zinc-900">
                <td className="py-1.5 pr-3 text-white/70">{row.label}</td>
                {row.cells.map((cell) => (
                  <td key={cell.date} className="py-1.5 px-2">
                    <div className="flex items-center justify-end gap-2">
                      <span
                        className={`text-[11px] tabular-nums ${VALUE_TONE[cell.status]}`}
                      >
                        {cell.display}
                      </span>
                      <span
                        className={`inline-block w-1.5 h-1.5 rounded-full ${DOT[cell.status]}`}
                        aria-hidden
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
